/* ============================================================================
   THE FIVE SCORES                                                    (PRD 4)

   "One overall number, and five scores underneath it that say what the number is
   made of. The overall is the average; the breakdown is where the work is."

     how well we serve them   what the gym has actually DONE for this member
     how well we know them    whether anyone in the building knows who they are
     consistency              whether they are holding their OWN rhythm
     community                whether they are pulling others in
     program progress         the five pillars of section 5

   This replaces three weighted families (attendance 0.40, anchors 0.35, health
   0.25). The old model was not wrong so much as narrow: it could say somebody had
   stopped coming and that nobody knew them, and it had no way at all to say the
   gym had done nothing for them.

   ── THE RULE THAT SHAPES THE WHOLE FILE ───────────────────────────────────
   A MISSING VALUE IS NOT A ZERO. "A figure nobody ever recorded is absent, not
   worst possible." And one level up: "A gym missing a whole family of signals must
   not be scored on it. Detect per gym, drop the family, rescale the rest to span 0
   to 100. Otherwise every member on a newly imported roster opens Red."

   So every input is `number | null`, null meaning UNKNOWN, and a score is the
   weighted average of the inputs it actually has, with the weights renormalised
   over those. An input nobody can answer contributes nothing rather than zero. A
   score with no inputs at all is null, and the overall is the average of the
   scores that survive. That is the whole mechanism, and it is what lets a gym that
   records none of this open on a sensible roster instead of an all-red one.

   ── A LOW SCORE IS A VERDICT ON THE GYM ────────────────────────────────────
   Two of the five are explicitly about the gym's own work: a member nobody has
   contacted scores low on "serve them", and a member nobody knows scores low on
   "know them". Neither is a fact about the member. That is the point of measuring
   it, and it is why this file never phrases an input as a failing of theirs.

   ── WHAT IT DOES NOT DO ───────────────────────────────────────────────────
   "Scores set priority and pace. They do not diagnose and they do not write the
   message." Nothing here returns a next action: that is ladder.ts, and the step
   comes from the situation rather than from the number.
   ============================================================================ */
import { daysSince, todayISO } from './time.js';
import { anchorStrength } from './anchors.js';
/* ── the arithmetic helpers ──────────────────────────────────────────────── */
const clamp01 = (n) => Math.min(1, Math.max(0, n));
/** A count against the number that counts as "enough". Null in, null out. */
function ratio(n, enough) {
    if (n == null)
        return null;
    return clamp01(n / enough);
}
/** A dated thing that decays: full marks today, nothing by `staleDays`. */
function recency(at, staleDays, today) {
    if (at == null)
        return null;
    const d = daysSince(at, today);
    if (d == null)
        return null;
    return clamp01(1 - d / staleDays);
}
/* THE WEIGHTED AVERAGE, OVER WHAT IS KNOWN. This is where "drop it and rescale"
   actually happens: unknown inputs are filtered out and the remaining weights are
   renormalised, so three known inputs out of five still span the full 0 to 100
   rather than capping at three fifths of it. */
function combine(inputs) {
    const known = inputs.filter((i) => i.value != null);
    if (!known.length)
        return null;
    const total = known.reduce((a, i) => a + i.weight, 0);
    if (total <= 0)
        return null;
    const v = known.reduce((a, i) => a + i.value * i.weight, 0) / total;
    return Math.round(clamp01(v) * 100);
}
const input = (key, label, weight, fills, value, why) => ({ key, label, weight, fills, value, ...(why ? { why } : {}) });
/* ── absence, against the member's OWN rhythm ────────────────────────────────
   Unchanged in spirit from the three-family model, because it was right: "a daily
   trainer and a fortnightly one are not the same at seven days missing". The
   window is a multiple of their own normal gap, clamped at both ends so it is
   neither twitchy for somebody who trains daily nor useless for somebody monthly. */
export const ABSENCE_FREE_MULTIPLE = 2;
export const ABSENCE_NAMED_MULTIPLE = 3;
export const ABSENCE_FREE_MIN = 5;
export const ABSENCE_FREE_MAX = 28;
export const ABSENCE_NAMED_MIN = 10;
export const ABSENCE_NAMED_MAX = 42;
export const ABSENCE_FREE_DAYS = 7;
export const ABSENCE_NAMED_DAYS = 14;
export const ABSENCE_RAMP_DAYS = 46;
const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n));
export function normalGapDays(m) {
    const rate = Number(m.priorVisitsWk) || Number(m.avgVisitsWk) || 0;
    return rate > 0 ? 7 / rate : null;
}
export function absenceWindow(m) {
    const gap = normalGapDays(m);
    if (gap == null)
        return { free: ABSENCE_FREE_DAYS, named: ABSENCE_NAMED_DAYS };
    return {
        free: clamp(gap * ABSENCE_FREE_MULTIPLE, ABSENCE_FREE_MIN, ABSENCE_FREE_MAX),
        named: clamp(gap * ABSENCE_NAMED_MULTIPLE, ABSENCE_NAMED_MIN, ABSENCE_NAMED_MAX),
    };
}
/** 1 when they are inside their own rhythm, 0 when they are long gone. */
function attendanceValue(m, today) {
    const dsv = daysSince(m.lastVisit, today);
    /* NO VISIT ON RECORD IS NOT UNKNOWN. A member with no visit is a member who has
       not been, which is a real reading and the worst one. Unknown would be a gym
       that does not send attendance at all, and that is `lastVisit === undefined`. */
    if (m.lastVisit === undefined)
        return { v: null };
    if (dsv == null)
        return { v: 0, why: 'No visit on record' };
    const w = absenceWindow(m);
    if (dsv <= w.free)
        return { v: 1 };
    const full = w.named + ABSENCE_RAMP_DAYS;
    const gone = clamp01((dsv - w.free) / (full - w.free));
    const why = dsv > w.named ? `${dsv}d since last visit${rhythmSuffix(m)}` : undefined;
    return { v: 1 - gone, ...(why ? { why } : {}) };
}
/**
 * Name THIS person's own pattern when the record carries one. "Was 3 a week, now 1"
 * is a journey; "Attendance and engagement slipping" was the same sentence on every
 * row. Moved here from risk.ts with the rest of the arithmetic; the generic wording
 * is still returned for records with no prior rate, which is the live tenant.
 */
export function cadenceLine(m, generic) {
    const prior = Number(m.priorVisitsWk);
    const now = Number(m.avgVisitsWk);
    if (!(prior > 0) || Number.isNaN(now) || prior - now < 0.8)
        return generic;
    const say = (v) => v < 0.35 ? 'hardly at all' : v < 1.5 ? 'about once a week' : Math.round(v) + ' a week';
    return 'Was ' + Math.round(prior) + ' a week, now ' + say(now);
}
/** "normally trains twice a week", when we know. Says whose absence this is. */
export function rhythmSuffix(m) {
    const rate = Number(m.priorVisitsWk) || Number(m.avgVisitsWk) || 0;
    if (!(rate > 0))
        return '';
    if (rate >= 5)
        return ' · normally trains most days';
    if (rate >= 1.5)
        return ` · normally ${Math.round(rate)} a week`;
    if (rate >= 0.8)
        return ' · normally about once a week';
    return ' · normally every couple of weeks';
}
/* ── the two heavy signals (PRD 4) ───────────────────────────────────────────
   "Two situations that look like nothing in the attendance data and predict a great
   deal. Both are INPUTS TO THE MODEL RATHER THAN TRAPDOORS OVER IT, weighted heavily
   enough to move a member on their own, so the record still shows its arithmetic and
   a coach can see the line that took the points off."

   That last clause is the design constraint. Neither one short-circuits the score;
   each is a named input inside the family it belongs to, carrying enough weight to
   drag that family down on its own. */
export const COMPLAINT_GRACE_DAYS = 7;
/** An anchor whose person has left the gym. Attendance has not fallen yet. */
export function severedAnchors(anchors) {
    return (anchors ?? []).filter((a) => a.severed === true);
}
export function scoreMember(m, opts = {}) {
    const today = opts.today ?? todayISO();
    const drop = new Set(opts.unsourced ?? []);
    const anchors = Array.isArray(m.anchors) ? m.anchors : [];
    /* ---- how well we serve them --------------------------------------------
       What the gym has DONE. Every input here is the gym's own work, so a low
       reading is a verdict on the gym and the wording says so. */
    const severed = severedAnchors(anchors);
    const complaintDays = daysSince(m.complaintOpenedAt, today);
    /* WHAT "ENOUGH" MEANS, and these are calibrations rather than product truths.
       Two contacts a quarter is a gym that is in touch; three was the first cut and
       it made an attentive gym read as a negligent one. One follow-up closed with an
       outcome is a real piece of work, not half of one. Two human facts is somebody
       the room knows something about. Move these when a real roster says otherwise;
       they are the one part of this file that is a judgement about gyms rather than
       a rule from the PRD. */
    const serve = [
        input('contact', 'Contact in the last 90 days', 3, 'Log a call, a message or a conversation against them.', ratio(m.contacts90d, 2), m.contacts90d === 0 ? 'Nobody here has contacted them in 90 days' : undefined),
        input('outcomes', 'Follow-ups closed with an outcome', 2, 'Close a follow-up with what came of it written down.', ratio(m.outcomesClosed90d, 1), m.outcomesClosed90d === 0 ? 'No follow-up closed with an outcome' : undefined),
        input('goalReview', 'Goal reviewed and current', 2, 'Set a goal and a review date on the Fitness Plan tab.', m.goalReviewDueAt == null ? null
            : (daysSince(m.goalReviewDueAt, today) ?? -1) > 0 ? 0 : 1, m.goalReviewDueAt != null && (daysSince(m.goalReviewDueAt, today) ?? -1) > 0
            ? 'Goal is past its review date' : undefined),
        input('recognition', 'Recognition given', 1, 'Mark a milestone or give a reward.', ratio(m.recognitions, 1)),
        /* THE COMPLAINT. Heavy enough to drag this score on its own past seven days,
           and it is an input rather than a trapdoor so the record can show the line. */
        /* THREE STATES, NOT TWO. `undefined` is a gym that does not record complaints
           at all and reads unknown; `null` is a gym that does and has none open for
           this member, which is full marks and is a different claim entirely. */
        input('complaint', 'No complaint left open', 4, 'This gym does not record complaints, so there is nothing to check.', m.complaintOpenedAt === undefined ? null
            : m.complaintOpenedAt === null ? 1
                : complaintDays == null ? null
                    : complaintDays > COMPLAINT_GRACE_DAYS ? 0 : 0.5, complaintDays != null && complaintDays > COMPLAINT_GRACE_DAYS
            ? `Complaint open ${complaintDays} days and not closed` : undefined),
    ];
    /* ---- how well we know them ---------------------------------------------- */
    const strong = anchors.filter((a) => anchorStrength(a, today) === 'strong').length;
    const know = [
        /* TWO IS THE BAR (PRD 4: "two or more is substantially self-sustaining"), AND
           AN ANCHOR HAS THREE STATES (SITE-RULES 2): none, decaying, live. A decaying
           one counts half, so all three readings are distinct and the arithmetic is
           (strong + half the decaying ones) against a bar of two.
    
           This reconciles two rules that do not quite agree. SITE-RULES 2 said one live
           anchor CLEARS the family; PRD 4, which is newer and explicit, puts the bar at
           two. The bar of two wins and the three states survive, which is the part of
           the older rule that was actually load-bearing: a relationship everybody has
           stopped speaking to is not the same as no relationship, and deleting the
           distinction is how "they know somebody" stays true long after it stopped. */
        input('anchors', 'Named, reciprocal relationships', 4, 'Record one on this page, under Who knows them.', m.anchors === undefined ? null
            : clamp01((strong + (anchors.length - strong) * 0.5) / 2), anchors.length === 0 ? 'No anchor on record'
            : strong === 0 ? 'Anchor going quiet' : undefined),
        /* THEIR INSTRUCTOR LEFT. "Attendance has not fallen yet; it will, in three to
           six weeks." Heavy, because nothing else in the data shows it at all. */
        /* NOT APPLICABLE WHEN THERE IS NO ANCHOR TO SEVER. Reading full marks for a
           member nobody is anchored to would hand three points of the family to the
           people who most need it to be low, which is precisely backwards. */
        input('severed', 'Their anchor still works here', 3, 'There is no anchor to lose yet, so there is nothing to read.', m.anchors === undefined || anchors.length === 0 ? null : severed.length ? 0 : 1, severed.length ? `${severed[0].name} has left, and they were anchored to them` : undefined),
        input('humanFacts', 'Human facts on record', 2, 'Write down anything they volunteered: what they do, who they came with, what they are training for.', ratio(m.humanFacts, 2), m.humanFacts === 0 ? 'Nobody has written down a single thing about them' : undefined),
        input('conversation', 'Conversation history', 1, 'Message them, and the thread appears on the Conversation tab.', ratio(m.conversation90d, 4)),
    ];
    /* ---- consistency --------------------------------------------------------
       Against their OWN baseline, never a gym average. */
    const att = attendanceValue(m, today);
    /* THEIR RATE AGAINST THEIR OWN PRIOR RATE. Separate from absence because they
       answer different questions: absence is "have they stopped", and this is "are
       they coming as often as they used to". Somebody who came four times a week and
       now comes once is still here, and is still the person to call. */
    const prior = Number(m.priorVisitsWk) || 0;
    const now = Number(m.avgVisitsWk) || 0;
    const consistency = [
        input('attendance', 'Holding their own rhythm', 5, 'Attendance comes from the booking system.', att.v, att.why),
        input('rate', 'Coming as often as they used to', 3, 'There is no earlier rate on record to compare this against.', prior > 0 ? clamp01(now / prior) : null, prior > 0 && prior - now >= 0.8 ? cadenceLine(m, 'Coming less often') : undefined),
        input('events', 'Turning up to things beyond class', 1, 'Invite them to an event and record who came.', m.eventsInvited ? ratio(m.eventsAttended, Math.max(1, m.eventsInvited)) : null),
    ];
    /* ---- community ----------------------------------------------------------
       TURNOUT CARRIES THIS, AND REFERRALS ARE THE BONUS. The first cut had it the
       other way round and the whole roster averaged 16: four members in five refer
       nobody, so a bar of "has referred somebody" made not-referring the floor and
       dragged every ordinary member into Amber.
  
       NOT REFERRING IS THE NORM, NOT THE FLOOR. A zero here has to mean as bad as
       this can be, and that is a member who gets invited to things, never comes, and
       brings nobody - not a member who turns up every week and has never happened to
       recommend the place. So showing up is weight three and the two rare positives
       are weight one each: a member who attends what they are asked to and refers
       nobody reads 60, which is what an ordinary good member should read. */
    const community = [
        input('eventTurnout', 'Turning up to things beyond class', 3, 'Invite them to an event and record who came.', m.eventsInvited ? ratio(m.eventsAttended, Math.max(1, m.eventsInvited)) : null),
        input('referrals', 'People they have brought in', 1, 'Record a referral on their page, in either direction.', ratio(m.referralsMade, 1)),
        input('guests', 'Guests brought', 1, 'Sign a guest in against their name.', ratio(m.guestsBrought, 1)),
    ];
    /* ---- program progress: the five pillars of section 5 --------------------
       Listed in the PRD's order of emphasis, which is deliberate: "the pillar the
       gym can act on today comes first, and the one the member owns mostly alone
       comes last". A pillar with no inputs is dropped and the rest rescale, exactly
       as the scores above are. */
    const scanDays = daysSince(m.lastScanAt, today);
    const macrosStale = m.macrosAt != null && m.lastScanAt != null && m.macrosAt < m.lastScanAt;
    const pillars = [
        pillar('social', 'Social connection', 'Do they have people here?', [
            input('anchors', 'Anchors', 3, 'record one on the record', m.anchors === undefined ? null
                : clamp01((strong + (anchors.length - strong) * 0.5) / 2)),
            input('events', 'Community turnout', 1, 'Invite them to an event and record who came.', m.eventsInvited ? ratio(m.eventsAttended, Math.max(1, m.eventsInvited)) : null),
            input('notes', 'Coach notes and conversation', 1, 'Leave a note or a message on their record.', ratio(m.conversation90d, 4)),
        ], drop),
        pillar('training', 'Training', 'Are they training, and is it working?', [
            input('attendance', 'Attendance', 3, 'Attendance comes from the booking system.', att.v),
            input('assessment', 'Movement and strength assessment', 2, 'Run an AMP movement and strength assessment.', recency(m.assessmentAt, 365, today)),
            input('wearables', 'Wearables', 1, 'No source today. Wearables arrive with the member app in phase 2.', null),
        ], drop),
        pillar('mindset', 'Mindset', 'Do they know why they are here?', [
            input('why', 'Why statement, in their words', 2, 'Ask at intake: it is the same conversation as the photo and the consent.', m.why === undefined ? null : m.why ? 1 : 0),
            input('goal', 'A goal with a live review date', 2, 'Set a goal and a review date on the Fitness Plan tab.', m.goalReviewDueAt == null ? (m.goal ? 0.5 : m.goal === undefined ? null : 0)
                : (daysSince(m.goalReviewDueAt, today) ?? -1) > 0 ? 0 : 1),
            input('labs', 'Labs and blood work', 1, 'No source today. Labs are not collected, and are far down the roadmap.', null),
        ], drop),
        /* NUTRITION IS COMPUTED, NOT COLLECTED. What is scored is whether targets are
           current against the latest scan, whether a plan exists against them, and
           whether the series is moving. NEVER intake: "the product never asks a member
           what they ate", and a pillar built on logging reads as failure for everyone
           who stopped logging rather than everyone who stopped eating well. */
        pillar('nutrition', 'Nutrition', 'Is the plan current, and is it working?', [
            input('scans', 'A current InBody scan', 2, 'Book a scan. The series is the evidence; a single scan is a photograph.', scanDays == null ? null : clamp01(1 - scanDays / 120)),
            input('macros', 'Targets current against that scan', 2, 'Compute targets on the Macros tab, from the latest scan and the goal.', m.macrosAt == null ? null : macrosStale ? 0 : 1),
            input('plan', 'A meal plan built to those targets', 1, 'Build a plan on the Macros tab, respecting what they cannot have.', m.mealPlanAt == null ? null : 1),
            input('moving', 'The series is moving toward the goal', 2, 'Two scans or more, so there is a direction to read.', m.scanSeriesMoving == null ? null : m.scanSeriesMoving ? 1 : 0),
        ], drop),
        pillar('recovery', 'Recovery', 'Are they recovering?', [
            input('practice', 'Logged practice', 1, 'No source today. Logged practice arrives with the member app.', ratio(m.recoveryLogged90d, 4)),
            input('sleep', 'Sleep and readiness', 1, 'No source today. Sleep and readiness arrive with the member app.', null),
        ], drop),
    ];
    const livePillars = pillars.filter((p) => p.value != null);
    const program = {
        key: 'program',
        label: 'Program progress',
        question: 'How are they doing across the five pillars?',
        value: livePillars.length
            ? Math.round(livePillars.reduce((a, p) => a + p.value, 0) / livePillars.length)
            : null,
        inputs: [],
        pillars,
    };
    const scores = [
        build('serve', 'How well we serve them', 'What has the gym actually done for them?', serve, drop),
        build('know', 'How well we know them', 'Does anyone here know who this person is?', know, drop),
        build('consistency', 'Consistency', 'Are they holding their own rhythm?', consistency, drop),
        build('community', 'Community', 'Are they pulling others in?', community, drop),
        program,
    ];
    /* THE OVERALL IS THE AVERAGE OF WHAT SURVIVED. Not of five: of however many the
       gym can actually answer, which is the rescale rule one level up. */
    const live = scores.filter((s) => s.value != null);
    const overall = live.length
        ? Math.round(live.reduce((a, s) => a + s.value, 0) / live.length)
        : null;
    return { overall, scores, unsourced: [...drop] };
}
function build(key, label, question, inputs, drop) {
    const kept = inputs.map((i) => (drop.has(`${key}.${i.key}`) ? { ...i, value: null } : i));
    return { key, label, question, value: combine(kept), inputs: kept };
}
function pillar(key, label, question, inputs, drop) {
    const kept = inputs.map((i) => (drop.has(`program.${key}.${i.key}`) ? { ...i, value: null } : i));
    return { key, label, question, value: combine(kept), inputs: kept };
}
/* ── asked once per gym (PRD 14) ─────────────────────────────────────────────
   "Ask once per gym whether a family of signals is modelled at all. If not, drop it
   and rescale the rest to span 0 to 100. The same applies to every native concept
   as it is introduced."

   The trap this closes is the one MIGRATION.md §9 already records: a newly imported
   gym has anchors on nobody, because no system it is migrating from has the concept.
   Scored naively that is the heaviest deduction on every member, and the roster
   opens entirely Red - "the product telling the truth about itself in the least
   useful possible way".

   AN INPUT IS UNSOURCED WHEN NOT ONE MEMBER IN THE GYM READS ABOVE ZERO ON IT.
   Evidence has to be a POSITIVE reading, not merely a present field: a roster where
   every member carries `anchors: []` is precisely the imported gym this exists for,
   and treating that as "the gym models anchors and all of them have none" is the
   whole failure. One member with an anchor means the gym IS modelling them, and the
   ones without are then a real finding.

   THE TRADE-OFF, stated rather than hidden: a gym where genuinely nobody has been
   contacted in ninety days is indistinguishable from one that does not log contacts,
   and this reads it as the second. That is the safer error. The first costs one
   missing line on a screen; the second opens the entire roster Red on the day a gym
   imports, which is the outcome MIGRATION.md §9 records and the reason any of this
   exists. */
export function detectSources(members, opts = {}) {
    const today = opts.today ?? todayISO();
    const seen = new Set();
    let any = false;
    let all = [];
    for (const m of members) {
        any = true;
        const r = scoreMember(m, { today });
        const walk = (prefix, s) => {
            for (const i of s.inputs)
                if (i.value != null && i.value > 0)
                    seen.add(`${prefix}${i.key}`);
            for (const p of s.pillars ?? [])
                walk(`${prefix}${p.key}.`, p);
        };
        for (const s of r.scores)
            walk(`${s.key}.`, s);
        if (!all.length) {
            const keys = [];
            const collect = (prefix, s) => {
                for (const i of s.inputs)
                    keys.push(`${prefix}${i.key}`);
                for (const p of s.pillars ?? [])
                    collect(`${prefix}${p.key}.`, p);
            };
            for (const s of r.scores)
                collect(`${s.key}.`, s);
            all = keys;
        }
    }
    /* An EMPTY roster sources nothing rather than everything: there is nothing yet to
       be wrong about, and dropping every input on a gym with no members would make
       the first member who joins unscoreable. */
    if (!any)
        return [];
    return all.filter((k) => !seen.has(k));
}
